/**
 * 录像回放引擎。
 *
 * 老系统的 .wtx 只有它自己的播放器能放；新格式是 JSONL 事件流，
 * 这里把它按时间轴重放成与直播时一致的房间状态。
 *
 * 设计要点：**任意跳转**。老播放器只能从头顺放（靠 1200 项关键帧表勉强跳转），
 * 这里因为事件流可全量载入内存，跳转就是「从头重算到目标时刻」——
 * 状态重建是纯函数，几千条事件毫秒级完成。
 */

import { MsgType } from './messages'
import type { WBObject, WBPage, WBSnapshot } from './whiteboard-types'

/** 录像里的一条事件。 */
export interface RecordedEvent {
  /** 相对录制开始的毫秒偏移 */
  t: number
  /** 消息类型 */
  k: string
  /** 载荷 */
  d?: unknown
}

/** 回放出的房间状态快照。 */
export interface PlaybackState {
  /** 白板页面 */
  pages: WBPage[]
  currentPage: number
  /** 聊天消息 */
  messages: Array<{ fromId: number; name: string; text: string; time: number; broadcast: boolean }>
  /** 在场成员：席位号 → 昵称 */
  members: Map<number, string>
  /** 各麦位的持有者 */
  mics: number[]
  /** 房间名 */
  roomName: string
}

/** 空状态。 */
function emptyState(): PlaybackState {
  return {
    pages: [{ index: 0, objects: [], lockBy: -2 }],
    currentPage: 0,
    messages: [],
    members: new Map(),
    mics: [],
    roomName: '',
  }
}

/**
 * Playback 持有整段事件流，可按任意时刻求出当时的房间状态。
 */
export class Playback {
  private events: RecordedEvent[] = []
  /** 总时长（毫秒） */
  private _duration = 0

  get duration(): number {
    return this._duration
  }

  get eventCount(): number {
    return this.events.length
  }

  /** 从 JSONL 文本载入。 */
  loadJSONL(text: string): void {
    const out: RecordedEvent[] = []
    for (const line of text.split('\n')) {
      const s = line.trim()
      if (!s) continue
      try {
        const e = JSON.parse(s) as RecordedEvent
        if (typeof e.t === 'number' && typeof e.k === 'string') out.push(e)
      } catch {
        // 录制中途断电可能留下半行，跳过即可，不该让整段录像放不了。
        continue
      }
    }
    out.sort((a, b) => a.t - b.t)
    this.events = out
    this._duration = out.length ? out[out.length - 1]!.t : 0
  }

  /** 求出 timeMs 时刻的房间状态。 */
  stateAt(timeMs: number): PlaybackState {
    const st = emptyState()
    for (const e of this.events) {
      if (e.t > timeMs) break
      applyEvent(st, e)
    }
    return st
  }

  /** 取指定时间区间内的事件，用于增量推进（避免每帧全量重算）。 */
  eventsBetween(fromMs: number, toMs: number): RecordedEvent[] {
    return this.events.filter((e) => e.t > fromMs && e.t <= toMs)
  }

  /** 事件的时间点列表，用于在进度条上标记关键节点。 */
  markers(): Array<{ t: number; label: string }> {
    const out: Array<{ t: number; label: string }> = []
    for (const e of this.events) {
      if (e.k === MsgType.WBPageAdd) out.push({ t: e.t, label: '新增白板页' })
      else if (e.k === MsgType.ShareStarted) out.push({ t: e.t, label: '开始共享' })
      else if (e.k === MsgType.MicAssigned) out.push({ t: e.t, label: '有人发言' })
    }
    return out
  }
}

/** 把一条事件应用到状态上。这是纯函数式的状态推进。 */
export function applyEvent(st: PlaybackState, e: RecordedEvent): void {
  switch (e.k) {
    case MsgType.RoomJoined: {
      const d = e.d as {
        room?: { name?: string; micNum?: number }
        users?: Array<{ id: number; name: string }>
        mics?: number[]
      }
      if (d.room?.name) st.roomName = d.room.name
      for (const u of d.users ?? []) st.members.set(u.id, u.name)
      if (d.mics) st.mics = [...d.mics]
      else if (d.room?.micNum) st.mics = new Array(d.room.micNum).fill(-2)
      break
    }

    case MsgType.WBSnapshot: {
      const d = e.d as WBSnapshot
      if (d.pages?.length) {
        // 深拷贝：状态推进过程中会就地改图元，不能污染原始事件数据。
        st.pages = d.pages.map((p) => ({ ...p, objects: p.objects.map((o) => ({ ...o })) }))
      }
      st.currentPage = d.currentPage ?? 0
      break
    }

    case MsgType.UserJoined: {
      const d = e.d as { id: number; name: string }
      st.members.set(d.id, d.name)
      break
    }

    case MsgType.UserLeft: {
      const d = e.d as { id: number }
      st.members.delete(d.id)
      break
    }

    case MsgType.TextRecv: {
      const d = e.d as {
        fromId: number
        name?: string
        text: string
        time: number
        broadcast?: boolean
        secret?: boolean
      }
      // 悄悄话不进录像回放 —— 录的是课堂，不是私聊。
      if (d.secret) break
      st.messages.push({
        fromId: d.fromId,
        name: d.name ?? st.members.get(d.fromId) ?? `席位 ${d.fromId}`,
        text: d.text,
        time: d.time,
        broadcast: !!d.broadcast,
      })
      break
    }

    case MsgType.WBOp: {
      applyWBOp(st, e.d as Record<string, unknown>)
      break
    }

    case MsgType.WBPageAdd: {
      const d = e.d as { page: number; title?: string }
      if (!st.pages.some((p) => p.index === d.page)) {
        st.pages.push({ index: d.page, title: d.title, objects: [], lockBy: -2 })
      }
      break
    }

    case MsgType.WBPageClear: {
      const d = e.d as { page: number }
      const p = st.pages.find((x) => x.index === d.page)
      if (p) p.objects = []
      break
    }

    case MsgType.WBPageGoto: {
      const d = e.d as { page: number }
      st.currentPage = d.page
      break
    }

    case MsgType.MicAssigned: {
      const d = e.d as { destId: number; micId: number }
      if (d.micId >= 0) {
        while (st.mics.length <= d.micId) st.mics.push(-2)
        st.mics[d.micId] = d.destId
      }
      break
    }

    case MsgType.MicReleased: {
      const d = e.d as { micId: number }
      if (d.micId >= 0 && d.micId < st.mics.length) st.mics[d.micId] = -2
      break
    }
  }
}

/** 把一条白板操作应用到状态上。 */
function applyWBOp(st: PlaybackState, d: Record<string, unknown>): void {
  const op = d.op as string
  const pageIdx = (d.page as number) ?? 0

  switch (op) {
    case 'add': {
      const obj = d.obj as WBObject | undefined
      if (!obj) return
      let page = st.pages.find((p) => p.index === obj.page)
      if (!page) {
        page = { index: obj.page, objects: [], lockBy: -2 }
        st.pages.push(page)
      }
      page.objects.push({ ...obj })
      break
    }
    case 'appendPt': {
      const id = d.objId as number
      const pts = d.points as Array<{ x: number; y: number }> | undefined
      if (!id || !pts) return
      for (const p of st.pages) {
        const o = p.objects.find((x) => x.id === id)
        if (o) {
          o.points = [...(o.points ?? []), ...pts]
          return
        }
      }
      break
    }
    case 'modify': {
      const obj = d.obj as WBObject | undefined
      if (!obj) return
      for (const p of st.pages) {
        const i = p.objects.findIndex((x) => x.id === obj.id)
        if (i >= 0) {
          p.objects[i] = { ...p.objects[i]!, ...obj }
          return
        }
      }
      break
    }
    case 'move': {
      const ids = new Set((d.objIds as number[]) ?? [])
      const delta = d.delta as { x: number; y: number } | undefined
      if (!delta) return
      for (const p of st.pages) {
        for (const o of p.objects) {
          if (ids.has(o.id)) {
            o.points = (o.points ?? []).map((pt) => ({ x: pt.x + delta.x, y: pt.y + delta.y }))
          }
        }
      }
      break
    }
    case 'delete': {
      const ids = new Set((d.objIds as number[]) ?? [])
      for (const p of st.pages) {
        p.objects = p.objects.filter((o) => !ids.has(o.id))
      }
      break
    }
    default:
      void pageIdx
  }
}
